import { useRef } from "react";
import { Carousel } from "@mantine/carousel";
import Autoplay from "embla-carousel-autoplay";
import { Box, rem } from "@mantine/core";
import {
  DiscoverPromoSlideCard,
  type DiscoverPromoCarouselBaseProps,
} from "./discoverPromoCarousel.shared";

export type DiscoverPromoCarouselDesktopProps = DiscoverPromoCarouselBaseProps & {
  /** Tighter slide padding for compact desktop hero layout. */
  compact?: boolean;
  /** Stretch carousel to fill parent height (desktop hero beside title/toggle). */
  fillHeight?: boolean;
};

export default function DiscoverPromoCarouselDesktop({
  slides,
  height = 200,
  autoplayDelayMs = 4500,
  compact = false,
  fillHeight = false,
}: DiscoverPromoCarouselDesktopProps) {
  const autoplay = useRef(
    Autoplay({
      delay: autoplayDelayMs,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
      stopOnFocusIn: false,
    }),
  );

  if (slides.length === 0) {
    return null;
  }

  const multi = slides.length > 1;
  const carouselHeight = fillHeight ? "100%" : height;
  const fullHeightStyles = fillHeight
    ? {
        root: { height: "100%" },
        viewport: { height: "100%" },
        container: { height: "100%" },
        slide: { height: "100%" },
      }
    : {};

  const carousel = (
    <Carousel
      height={carouselHeight}
      w="100%"
      maw="100%"
      withControls={multi}
      withIndicators={multi}
      loop={multi}
      plugins={multi ? [autoplay.current] : []}
      onMouseEnter={multi ? autoplay.current.stop : undefined}
      onMouseLeave={multi ? autoplay.current.reset : undefined}
      controlSize={compact ? 20 : 22}
      controlsOffset={compact ? 2 : 4}
      styles={{
        root: {
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          ...fullHeightStyles.root,
        },
        viewport: { maxWidth: "100%", boxSizing: "border-box", ...fullHeightStyles.viewport },
        container: { maxWidth: "100%", ...fullHeightStyles.container },
        controls: {
          paddingInline: 0,
        },
        control: {
          opacity: 0.92,
        },
        indicators: { bottom: compact ? rem(4) : rem(8) },
        indicator: { width: 6, height: 6 },
        slide: fullHeightStyles.slide,
      }}
    >
      {slides.map((slide) => (
        <Carousel.Slide key={slide.id}>
          <DiscoverPromoSlideCard slide={slide} multi={multi} compact={compact} />
        </Carousel.Slide>
      ))}
    </Carousel>
  );

  if (fillHeight) {
    return (
      <Box h="100%" w="100%" style={{ flex: 1, minHeight: 0 }}>
        {carousel}
      </Box>
    );
  }

  return carousel;
}
